
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAuth } from "@/contexts/AuthContext";
import { signupFormSchema, SignupFormValues } from "./schemas/signupFormSchema";
import { AgeRangeSelector } from "./form-fields/AgeRangeSelector";
import { EducationLevelSelector } from "./form-fields/EducationLevelSelector";
import { CountrySelector } from "./form-fields/CountrySelector";

interface SignUpFormProps {
  onSuccess: () => void;
  onSwitchMode: () => void;
  redirectPath?: string;
}

export function SignUpForm({ onSuccess, onSwitchMode, redirectPath }: SignUpFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { signup } = useAuth();

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupFormSchema),
    defaultValues: {
      email: "",
      password: "",
      ageRange: "",
      educationLevel: "",
      country: "United States",
    },
  });

  const handleSubmit = async (data: SignupFormValues) => {
    setIsLoading(true);
    
    try {
      await signup(data.email, data.password, data.ageRange, data.educationLevel, data.country);
      
      form.reset();
      onSuccess();
      
      if (redirectPath) {
        window.location.href = redirectPath;
      }
    } catch (error) {
      console.error("Authentication error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 pt-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="your.email@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <Input type="password" placeholder="••••••••" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <AgeRangeSelector form={form} />
        <EducationLevelSelector form={form} />
        <CountrySelector form={form} />
        
        <div className="flex flex-col gap-4 pt-2">
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating account...
              </span>
            ) : (
              <>Create account</>
            )}
          </Button>
          
          <Button type="button" variant="link" onClick={onSwitchMode} className="text-sm text-muted-foreground">
            Already have an account? Sign in
          </Button>
        </div>
      </form>
    </Form>
  );
}
